import { useCallback, useEffect, useRef, useState } from "react";
import { playSeasonSound } from "@/lib/seasonSounds";

// Surprise cachée (easter egg) de l'événement en cours : un petit objet discret sur la
// page (la citrouille d'Halloween en haut à gauche…). Au clic : une animation et un son,
// puis tout disparaît. Le panel admin peut la déclencher (événement `vp-season-egg`).

const UNDER_NAV = 84;
const EGGS = {
  halloween: { icon: "🎃", pos: { left: 16, top: UNDER_NAV }, kind: "eyes", sound: "laugh" },
  noel: { icon: "🎁", pos: { right: 18, top: UNDER_NAV + 8 }, kind: "sleigh", sound: "bells" },
  "saint-nicolas": { icon: "🎁", pos: { right: 18, top: UNDER_NAV + 8 }, kind: "sleigh", sound: "bells" },
  "nouvel-an": { icon: "🍾", pos: { left: 16, top: "46%" }, kind: "burst", set: ["🎉", "🥂", "✨", "🎊", "⭐"], text: "Santé ! 🥂", sound: "pop" },
  "fetes-fin-annee": { icon: "🍾", pos: { left: 16, top: "46%" }, kind: "burst", set: ["🎉", "🥂", "✨", "🎊", "⭐"], text: "Santé ! 🥂", sound: "pop" },
  "saint-valentin": { icon: "💘", pos: { right: 18, top: "38%" }, kind: "burst", set: ["❤️", "💖", "💕", "💘", "🌹"], text: "Tu as trouvé mon cœur 💘", sound: "chime" },
  paques: { icon: "🥚", pos: { left: 16, top: "58%" }, kind: "burst", set: ["🐣", "🍫", "🥚", "🌷", "🐰"], text: "Cui cui ! 🐣", hatched: "🐣", sound: "chirp" },
  "poisson-avril": { icon: "🐟", pos: { right: 18, top: "52%" }, kind: "fish", text: "Poisson d'avril ! 🐟", sound: "blub" },
  "14-juillet": { icon: "🇫🇷", pos: { left: 16, top: UNDER_NAV }, kind: "burst", set: ["🎆", "🇫🇷", "✨", "🎇"], text: "Vive la République ! 🇫🇷", sound: "fanfare" },
  "star-wars": { icon: "🌌", pos: { right: 18, top: UNDER_NAV + 8 }, kind: "burst", set: ["⭐", "✨", "🪐", "🌌"], text: "Que la Force soit avec toi.", sound: "saber" },
};
const SPOTS = [{ left: 16, top: UNDER_NAV }, { right: 18, top: "40%" }, { right: 18, top: UNDER_NAV + 8 }, { left: 16, top: "55%" }];

function eggFor(season) {
  if (!season) return null;
  const known = EGGS[season.event.id];
  if (known) return known;
  const hash = [...season.event.id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);
  return { icon: season.emoji, pos: SPOTS[hash % SPOTS.length], kind: "burst", set: [season.emoji, "✨", "🎉"], text: `${season.title} ${season.emoji}`, sound: "sparkle" };
}

const STYLE = `
@keyframes vp-egg-bob { 0%,100% { transform: translateY(0) rotate(-6deg); } 50% { transform: translateY(-4px) rotate(6deg); } }
@keyframes vp-egg-shake { 0%,100% { transform: rotate(0); } 25% { transform: rotate(-18deg) scale(1.15); } 75% { transform: rotate(18deg) scale(1.15); } }
@keyframes vp-egg-dark { 0% { opacity: 0; } 12% { opacity: 1; } 82% { opacity: 1; } 100% { opacity: 0; } }
@keyframes vp-egg-eyes { 0% { opacity: 0; transform: scale(0.6); } 15% { opacity: 1; transform: scale(1); } 85% { opacity: 1; } 100% { opacity: 0; } }
@keyframes vp-egg-blink { 0%,44%,52%,100% { transform: scaleY(1); } 48% { transform: scaleY(0.08); } }
@keyframes vp-santa-bob { from { translate: 0 -5px; rotate: -2deg; } to { translate: 0 5px; rotate: 2deg; } }
@keyframes vp-santa-gallop { from { transform: translateY(-3px) rotate(-4deg); } to { transform: translateY(3px) rotate(4deg); } }
@keyframes vp-santa-wave { from { transform: rotate(-12deg); } to { transform: rotate(18deg); } }
@keyframes vp-egg-pop { from { transform: scale(0.4); opacity: 0; } to { transform: scale(1); opacity: 1; } }
@keyframes vp-egg-text { 0% { opacity: 0; transform: translateY(8px) scale(0.9); } 15%,80% { opacity: 1; transform: translateY(0) scale(1); } 100% { opacity: 0; transform: translateY(-10px); } }
@keyframes vp-egg-fish { 0% { opacity: 0; transform: translateX(-50%) translateY(40px) rotate(-8deg); } 15% { opacity: 1; transform: translateX(-50%) translateY(0) rotate(6deg); } 30%,50%,70% { transform: translateX(-50%) rotate(-6deg); } 40%,60%,80% { transform: translateX(-50%) rotate(6deg); } 100% { opacity: 0; transform: translateX(-50%) translateY(30px); } }
`;

function Eyes() {
  // Paires d'yeux rouges qui s'allument une à une dans le noir.
  const [pairs] = useState(() => Array.from({ length: 9 }, (_, i) => ({
    left: 6 + Math.random() * 84, top: 8 + Math.random() * 80, size: 10 + Math.random() * 14,
    delay: 0.25 + i * 0.16, blink: 1.6 + Math.random() * 1.6,
  })));
  return (
    <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 80, background: "radial-gradient(ellipse at center, rgba(10,0,0,0.82), rgba(0,0,0,0.94))", animation: "vp-egg-dark 3.8s ease forwards" }} data-testid="egg-eyes">
      {pairs.map((p, i) => (
        <div key={i} className="absolute flex" style={{ left: `${p.left}%`, top: `${p.top}%`, gap: p.size * 0.9, opacity: 0, animation: `vp-egg-eyes 3.3s ease ${p.delay}s forwards` }}>
          {[0, 1].map((k) => (
            <span key={k} style={{
              display: "block", width: p.size * 1.6, height: p.size, borderRadius: k ? "40% 60% 50% 50%" : "60% 40% 50% 50%",
              background: "radial-gradient(circle at 50% 55%, #fff4f4 0 8%, #ff2a2a 22%, #b00000 60%, transparent 72%)",
              boxShadow: `0 0 ${p.size}px ${p.size / 2}px rgba(255,0,0,0.55)`,
              transform: `rotate(${k ? -12 : 12}deg)`, animation: `vp-egg-blink ${p.blink}s ease-in-out infinite`,
            }} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ---------- Noël : le Père Noël en traîneau (dessin SVG) ----------

const GIFT_COLORS = [["#e11d48", "#be123c"], ["#2563eb", "#1d4ed8"], ["#16a34a", "#15803d"], ["#9333ea", "#7e22ce"], ["#f97316", "#ea580c"]];
const giftSvg = ([c, c2]) => `<svg viewBox="0 0 40 42" width="100%" height="100%"><rect x="5" y="14" width="30" height="26" rx="3" fill="${c}"/><rect x="3" y="10" width="34" height="8" rx="2" fill="${c2}"/><rect x="17" y="10" width="6" height="30" fill="#f5c542"/><path d="M20 10 C12 0 4 7 20 10 C36 7 28 0 20 10" fill="none" stroke="#f5c542" stroke-width="3" stroke-linecap="round"/></svg>`;

export function GiftIcon({ size = 26 }) {
  return <span style={{ display: "inline-block", width: size, height: size }} dangerouslySetInnerHTML={{ __html: giftSvg(GIFT_COLORS[0]) }} />;
}

function Reindeer({ x, rudolph, delay }) {
  return (
    <g transform={`translate(${x} 96)`}>
      <g style={{ animation: `vp-santa-gallop 0.5s ease-in-out ${delay}s infinite alternate`, transformBox: "fill-box", transformOrigin: "center" }}>
        <path d="M-26 10 L-42 30 M-18 12 L-32 33" stroke="#6b4423" strokeWidth="5" strokeLinecap="round" />
        <path d="M24 9 L42 25 M31 6 L50 19" stroke="#6b4423" strokeWidth="5" strokeLinecap="round" />
        <ellipse cx="0" cy="0" rx="38" ry="16" fill="#9a6233" />
        <ellipse cx="2" cy="6" rx="26" ry="8" fill="#b9814f" />
        <ellipse cx="38" cy="-6" rx="6" ry="4" fill="#f3e6d3" />
        <polygon points="-28,-10 -42,-36 -31,-40 -17,-8" fill="#9a6233" />
        <path d="M-32 -16 L-24 -8" stroke="#f5c542" strokeWidth="5" />
        <ellipse cx="-46" cy="-39" rx="13" ry="9" fill="#9a6233" />
        <ellipse cx="-57" cy="-36" rx="8" ry="6" fill="#b9814f" />
        <circle cx="-64" cy="-37" r={rudolph ? 5 : 3.5} fill={rudolph ? "#ff2d2d" : "#3b2414"} style={rudolph ? { filter: "drop-shadow(0 0 6px #ff4d4d)" } : undefined} />
        <circle cx="-47" cy="-42" r="2" fill="#1a1a1a" />
        <ellipse cx="-37" cy="-45" rx="7" ry="3" fill="#7d4c26" transform="rotate(-25 -37 -45)" />
        <path d="M-45 -47 L-41 -64 M-42 -57 L-50 -66 M-41 -64 L-33 -71 M-50 -46 L-57 -61 M-55 -56 L-63 -60" stroke="#5b3a1a" strokeWidth="3" strokeLinecap="round" fill="none" />
      </g>
    </g>
  );
}

function SantaSleigh() {
  return (
    <svg viewBox="0 0 520 170" width="100%" height="100%" style={{ overflow: "visible", filter: "drop-shadow(0 8px 14px rgba(0,0,0,0.45))" }}>
      <path d="M40 92 L170 92 L300 92" stroke="#f5c542" strokeWidth="2.5" fill="none" />
      <Reindeer x={80} rudolph delay={0} />
      <Reindeer x={205} delay={0.25} />
      <g transform="translate(290 40)">
        {/* hotte */}
        <ellipse cx="168" cy="6" rx="24" ry="28" fill="#8b5e34" />
        <rect x="150" y="-26" width="16" height="14" rx="2" fill="#16a34a" />
        <rect x="166" y="-32" width="14" height="18" rx="2" fill="#2563eb" />
        <path d="M150 -10 Q168 -18 188 -10" stroke="#6b4423" strokeWidth="4" fill="none" />
        {/* Père Noël */}
        <ellipse cx="125" cy="12" rx="27" ry="25" fill="#d62828" />
        <rect x="99" y="14" width="52" height="8" fill="#1f1f1f" />
        <rect x="120" y="13" width="10" height="10" fill="#f5c542" />
        <path d="M108 0 L84 -24" stroke="#d62828" strokeWidth="11" strokeLinecap="round" style={{ animation: "vp-santa-wave 0.6s ease-in-out infinite alternate", transformBox: "fill-box", transformOrigin: "100% 100%" }} />
        <circle cx="117" cy="-22" r="13" fill="#f6c9a0" />
        <path d="M103 -20 Q104 4 117 6 Q131 4 131 -20 Q124 -12 117 -14 Q110 -12 103 -20 Z" fill="#ffffff" />
        <circle cx="112" cy="-25" r="1.8" fill="#1a1a1a" />
        <circle cx="122" cy="-25" r="1.8" fill="#1a1a1a" />
        <circle cx="117" cy="-19" r="3" fill="#f08a7a" />
        <path d="M103 -30 Q117 -60 140 -52 L131 -30 Z" fill="#d62828" />
        <rect x="101" y="-33" width="32" height="7" rx="3.5" fill="#ffffff" />
        <circle cx="142" cy="-52" r="6" fill="#ffffff" />
        {/* traîneau */}
        <path d="M14 20 Q2 72 42 76 L150 76 Q182 76 186 40 L190 18 Z" fill="#c81e1e" />
        <path d="M14 20 L190 18" stroke="#f5c542" strokeWidth="5" strokeLinecap="round" />
        <path d="M14 20 Q-8 2 8 -8 Q22 -12 20 6" stroke="#f5c542" strokeWidth="5" fill="none" strokeLinecap="round" />
        <path d="M40 76 L36 90 M150 76 L154 90" stroke="#a37c1d" strokeWidth="4" />
        <path d="M-6 92 L176 92 Q200 92 198 72" stroke="#f5c542" strokeWidth="5" fill="none" strokeLinecap="round" />
        <path d="M-6 92 Q-20 92 -18 80" stroke="#f5c542" strokeWidth="5" fill="none" strokeLinecap="round" />
      </g>
    </svg>
  );
}

const SANTA_PASS_MS = 4200;

// Deux passages (droite → gauche puis gauche → droite), « Oh oh oh ! » plusieurs fois,
// et des cadeaux qui tombent du traîneau.
function SantaRun() {
  const wrapRef = useRef(null);
  const [pass, setPass] = useState(0);
  const [talking, setTalking] = useState(false);
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const w = window.innerWidth, h = window.innerHeight;
    const width = Math.min(460, w * 0.72);
    const timers = [];
    const later = (fn, ms) => timers.push(setTimeout(fn, ms));
    let stopped = false;
    const run = (index) => {
      if (stopped) return;
      setPass(index);
      const leftward = index === 0;
      const y = leftward ? h * 0.17 : h * 0.32;
      const from = leftward ? w + 30 : -width - 30;
      const to = leftward ? -width - 30 : w + 30;
      wrap.animate([
        { transform: `translate(${from}px, ${y}px)` },
        { transform: `translate(${(from + to) / 2}px, ${y - h * 0.05}px)` },
        { transform: `translate(${to}px, ${y + h * 0.02}px)` },
      ], { duration: SANTA_PASS_MS, easing: "linear", fill: "forwards" });
      playSeasonSound("bells");
      [250, 1550, 2850].forEach((ms) => later(() => { setTalking(true); playSeasonSound("hohoho"); later(() => setTalking(false), 1100); }, ms));
      if (index === 0) later(() => run(1), SANTA_PASS_MS + 300);
    };
    run(0);
    // Cadeaux lâchés depuis la hotte.
    const drop = setInterval(() => {
      const r = wrap.getBoundingClientRect();
      if (r.right < 0 || r.left > w) return;
      const leftward = wrap.dataset.pass === "0";
      const x = r.left + r.width * (leftward ? 0.9 : 0.1);
      const startY = r.top + r.height * 0.3;
      const gift = document.createElement("div");
      const size = 26 + Math.random() * 14;
      gift.style.cssText = `position:fixed;left:${x}px;top:${startY}px;width:${size}px;height:${size}px;z-index:81;pointer-events:none;filter:drop-shadow(0 3px 5px rgba(0,0,0,.4))`;
      gift.innerHTML = giftSvg(GIFT_COLORS[Math.floor(Math.random() * GIFT_COLORS.length)]);
      document.body.appendChild(gift);
      const drift = (leftward ? 1 : -1) * (40 + Math.random() * 60);
      const fall = h * 0.55 + Math.random() * h * 0.25;
      const spin = (Math.random() - 0.5) * 540;
      gift.animate([
        { transform: "translate(-50%, 0) rotate(0deg)", opacity: 1 },
        { transform: `translate(calc(-50% + ${drift}px), ${fall}px) rotate(${spin}deg)`, opacity: 1, offset: 0.85 },
        { transform: `translate(calc(-50% + ${drift * 1.1}px), ${fall + 20}px) rotate(${spin}deg)`, opacity: 0 },
      ], { duration: 1700 + Math.random() * 500, easing: "cubic-bezier(.45,0,.8,.6)", fill: "forwards" });
      setTimeout(() => gift.remove(), 2400);
    }, 330);
    return () => {
      stopped = true;
      clearInterval(drop);
      timers.forEach(clearTimeout);
    };
  }, []);
  const width = Math.min(460, (typeof window !== "undefined" ? window.innerWidth : 1000) * 0.72);
  const leftward = pass === 0;
  return (
    <div ref={wrapRef} data-pass={pass} className="fixed left-0 top-0 pointer-events-none select-none" style={{ zIndex: 80, width, height: width * (170 / 520), transform: "translate(-9999px, 0)" }} data-testid="egg-sleigh">
      <div style={{ width: "100%", height: "100%", transform: leftward ? "none" : "scaleX(-1)", animation: "vp-santa-bob 0.9s ease-in-out infinite alternate" }}>
        <SantaSleigh />
      </div>
      {talking && (
        <div className="absolute whitespace-nowrap rounded-2xl bg-white text-[#b91c1c] font-extrabold px-3 py-1.5 shadow-lg" style={{ top: -38, [leftward ? "left" : "right"]: "62%", fontSize: Math.max(14, width * 0.045), animation: "vp-egg-pop 0.25s ease-out" }} data-testid="egg-hohoho">
          Oh oh oh !
        </div>
      )}
    </div>
  );
}

function burstFrom(x, y, set) {
  // Explosion d'emojis depuis l'objet cliqué (Web Animations, rien à nettoyer à la main).
  const layer = document.createElement("div");
  layer.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:80";
  document.body.appendChild(layer);
  for (let i = 0; i < 30; i++) {
    const s = document.createElement("span");
    s.textContent = set[i % set.length];
    s.style.cssText = `position:absolute;left:${x}px;top:${y}px;font-size:${16 + Math.random() * 18}px;line-height:1;will-change:transform,opacity`;
    layer.appendChild(s);
    const angle = Math.random() * Math.PI * 2;
    const dist = 90 + Math.random() * 260;
    const dx = Math.cos(angle) * dist, dy = Math.sin(angle) * dist - 60;
    s.animate([
      { transform: "translate(-50%,-50%) scale(0.3) rotate(0deg)", opacity: 1 },
      { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(1) rotate(${(Math.random() - 0.5) * 540}deg)`, opacity: 1, offset: 0.7 },
      { transform: `translate(calc(-50% + ${dx * 1.1}px), calc(-50% + ${dy + 120}px)) scale(0.9) rotate(${(Math.random() - 0.5) * 720}deg)`, opacity: 0 },
    ], { duration: 1500 + Math.random() * 700, easing: "cubic-bezier(.2,.7,.3,1)", fill: "forwards" });
  }
  setTimeout(() => layer.remove(), 2600);
}

export default function SeasonEasterEgg({ season }) {
  const egg = eggFor(season);
  const ref = useRef(null);
  const [active, setActive] = useState(null);
  const busy = useRef(false);

  const trigger = useCallback(() => {
    if (!egg || busy.current) return;
    busy.current = true;
    const rect = ref.current ? ref.current.getBoundingClientRect() : { left: window.innerWidth / 2, top: window.innerHeight / 2, width: 0, height: 0 };
    const x = rect.left + rect.width / 2, y = rect.top + rect.height / 2;
    if (egg.kind !== "sleigh") playSeasonSound(egg.sound);
    if (egg.kind === "burst") burstFrom(x, y, egg.set);
    const duration = egg.kind === "eyes" ? 3900 : egg.kind === "sleigh" ? SANTA_PASS_MS * 2 + 900 : 3000;
    setActive({ kind: egg.kind, text: egg.text, x, y, id: Date.now() });
    setTimeout(() => { setActive(null); busy.current = false; }, duration);
  }, [egg]);

  useEffect(() => {
    const onEgg = () => trigger();
    window.addEventListener("vp-season-egg", onEgg);
    return () => window.removeEventListener("vp-season-egg", onEgg);
  }, [trigger]);

  if (!egg) return null;
  const textLeft = active ? Math.min(Math.max(active.x, 140), window.innerWidth - 140) : 0;
  return (
    <>
      <style>{STYLE}</style>
      <button
        ref={ref}
        type="button"
        onClick={trigger}
        aria-label="Surprise"
        data-testid="season-egg"
        className="fixed select-none leading-none opacity-50 hover:opacity-100 focus:opacity-100 transition-opacity"
        style={{ zIndex: 46, fontSize: 26, padding: 4, background: "none", border: 0, cursor: "pointer", ...egg.pos, animation: active ? "vp-egg-shake 0.5s ease 2" : "vp-egg-bob 3.2s ease-in-out infinite" }}
      >
        {egg.kind === "sleigh" ? <GiftIcon /> : active && egg.hatched ? egg.hatched : egg.icon}
      </button>
      {active && active.kind === "eyes" && <Eyes key={active.id} />}
      {active && active.kind === "sleigh" && <SantaRun key={active.id} />}
      {active && active.kind === "fish" && (
        <div key={active.id} className="fixed pointer-events-none text-center select-none" style={{ zIndex: 80, left: "50%", bottom: 24, animation: "vp-egg-fish 3s ease forwards", opacity: 0 }} data-testid="egg-fish">
          <div style={{ fontSize: 64, transform: "scaleX(-1)" }}>🐟</div>
          <div className="text-white font-bold text-xl mt-1" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.7)" }}>{active.text}</div>
        </div>
      )}
      {active && active.kind === "burst" && active.text && (
        <div key={active.id} className="fixed pointer-events-none text-white font-bold text-xl whitespace-nowrap" style={{ zIndex: 80, left: textLeft, top: Math.max(active.y - 70, 70), animation: "vp-egg-text 2.8s ease forwards", opacity: 0, textShadow: "0 2px 12px rgba(0,0,0,0.7)" }} data-testid="egg-text">
          <span style={{ display: "inline-block", transform: "translateX(-50%)" }}>{active.text}</span>
        </div>
      )}
    </>
  );
}
